/**
 * El estado de preparación de los datos, para el cierre del túnel.
 *
 * El túnel promete que la agencia consulta desde su IA en el mismo flujo, y una agencia nueva nace
 * sin datos sincronizados: si pregunta antes de tiempo, la IA le contesta vacío y el momento que
 * decide todo se vuelve la primera decepción. La pantalla espera —esa es la decisión tomada— pero
 * la espera **tiene que decir qué está pasando**: una espera visible y muda es peor que no esperar.
 *
 * Dos cosas que este módulo NO hace, a propósito:
 *
 * - **No estima un tiempo.** Se midió contra producción: mediana de 0 minutos contra p95 de 60,
 *   sobre las mismas corridas. Con esa varianza, cualquier barra se clava o miente. Lo único que se
 *   muestra es un hecho verificable: cuántas cuentas ya están listas.
 * - **No inventa estado.** Si la respuesta no se puede usar, el bloque desaparece (`HIDDEN`) en vez
 *   de mostrar un spinner sin explicación — pero desaparecer no es rendirse: mientras queden
 *   reintentos se sigue preguntando, y el bloque vuelve solo cuando el backend contesta.
 *
 * La lógica vive acá y no en el componente para poder testearla sin red ni React, igual que
 * `budgetMath` y `proposalListView`.
 */

/** Trayendo los datos de las cuentas: todavía no se puede preguntar con confianza. */
export const PREPARING = 'PREPARING';
/** El sync terminó pero el MERGE a BigQuery se difiere 60s, y la IA lee de ahí. */
export const FLUSHING = 'FLUSHING';
/** Se puede preguntar. */
export const READY = 'READY';
/** Se agotó la espera y sigue faltando: se deja de prometer y se dice qué pasa. */
export const BACKGROUND = 'BACKGROUND';
/** El plan de la agencia no incluye el acceso de la IA a los datos: no va a terminar nunca. */
export const UNSUPPORTED = 'UNSUPPORTED';
/** No hay estado confiable. No se muestra nada. */
export const HIDDEN = 'HIDDEN';

const SERVER_PHASES = {
    PREPARING: PREPARING,
    FLUSHING: FLUSHING,
    READY: READY,
    NOT_APPLICABLE: UNSUPPORTED,
};

const TERMINAL = [READY, BACKGROUND, UNSUPPORTED, HIDDEN];

/**
 * La respuesta del backend, leída sin creerle de más.
 *
 * La rama de error se escribe `status !== 'OK'` y no `status === 'ERROR'`: el backend manda su
 * código tipado en `status`, así que un `NOT_FOUND` no vale `'ERROR'` y con la comparación por
 * igualdad se colaría como si fuera una respuesta buena.
 */
export function readinessFrom(response) {
    if (!response || response.status !== 'OK') return {available: false};

    const data = response.data || {};
    const phase = SERVER_PHASES[data.phase];
    // Una fase que este frontend no conoce no se adivina: el backend puede deployar antes.
    //
    // Pero «no sé qué fase es» y «no pude hablar con el backend» son dos cosas distintas, y
    // confundirlas rompía el contrato del propio backend: cuando el cálculo le falla, responde 200
    // con la fase vacía y `pending: true` justamente para decir «todavía no sé, seguí preguntando».
    // Contando eso como fallo, veinte segundos de backend enfermo apagaban el bloque para el resto
    // de la sesión — el mismo defecto que este módulo vino a arreglar.
    if (!phase) return {available: false, serverIsStillWorking: Boolean(data.poll && data.poll.pending)};

    return {
        available: true,
        phase,
        ready: Number(data.accounts_ready) || 0,
        total: Number(data.accounts_total) || 0,
        largePortfolio: Boolean(data.large_portfolio),
        pending: Boolean(data.poll && data.poll.pending),
        retryAfterSeconds: Number(data.poll && data.poll.retry_after_seconds) || null,
    };
}

/**
 * Qué muestra la pantalla, y si tiene sentido volver a preguntar.
 *
 * `BACKGROUND` es el estado terminal por tiempo, y existe por un agujero real: la marca que saca a
 * las cuentas muertas del cómputo se estampa recién cuando algo intenta sincronizarlas, y en el
 * túnel el primer proceso que las toca es el del paso anterior. En la primera pasada de una agencia
 * nueva —el único onboarding que existe— algunas entran al total igual, así que el total puede no
 * alcanzarse nunca. Antes que esperar un número imposible, pasado el tope se dice lo que es cierto:
 * ya se puede preguntar, y lo que falta va a aparecer solo.
 */
export function syncStateOf(readiness, {elapsedMs = 0, maxWaitMs = Infinity} = {}) {
    const nothingToShow = (keepPolling) => ({kind: HIDDEN, ready: 0, total: 0, largePortfolio: false,
                                             keepPolling, retryAfterSeconds: null});

    // Sin dato usable: no se muestra nada, pero se sigue preguntando mientras quede tiempo. El
    // contador de fallos ya NO apaga el poll —espacia la cadencia, ver `retryDelayFor`—: apagarlo
    // dejaba a la agencia sin el bloque por el resto de la sesión aunque el backend se recuperara a
    // los treinta segundos, y recargar la página no es algo que se le pueda pedir.
    if (!readiness || !readiness.available) return nothingToShow(elapsedMs < maxWaitMs);

    const isTerminal = TERMINAL.indexOf(readiness.phase) >= 0;
    const kind = (!isTerminal && elapsedMs >= maxWaitMs) ? BACKGROUND : readiness.phase;

    return {
        kind,
        ready: readiness.ready,
        total: readiness.total,
        largePortfolio: readiness.largePortfolio,
        // `pending` significa «todavía puede cambiar», nunca «falta»: derivarlo del contador
        // dejaría la pantalla preguntando para siempre por una cuenta que no va a llegar.
        keepPolling: TERMINAL.indexOf(kind) < 0 && readiness.pending,
        retryAfterSeconds: readiness.retryAfterSeconds,
    };
}

/**
 * Cuánto esperar antes de volver a preguntar, en segundos.
 *
 * Con el backend sano manda el servidor (`retry_after_seconds`), que es donde vive el knob. Con el
 * backend enfermo la cadencia se abre —10 s, 30 s, 60 s y ahí queda— en vez de apagarse: el bloque
 * vuelve solo cuando el backend se recupera, y mientras tanto un servidor que ya está sufriendo no
 * recibe un pedido cada diez segundos de cada agencia que esté dándose de alta.
 */
export function retryDelayFor(syncState, failures, {base = 10, steps = [10, 30, 60]} = {}) {
    if (syncState && syncState.retryAfterSeconds && !failures) return syncState.retryAfterSeconds;
    if (!failures) return base;
    return steps[Math.min(failures, steps.length) - 1];
}

/**
 * La clave i18n de la frase, y si lleva contador.
 *
 * Sin cuentas que esperar no se dice «listo»: un «0 de 0 listas» no informa nada y suena a error.
 * Y `READY` con cuentas faltantes tiene su propia frase — es un caso real (una cuenta que nunca
 * sincronizó no bloquea la consulta) y decir «listo» a secas dejaría al usuario sin saber por qué
 * el número no cerró.
 */
export function messageKeyOf(syncState) {
    if (!syncState || syncState.kind === HIDDEN) return null;
    if (syncState.kind === UNSUPPORTED) return 'onboarding.ia.sync.unsupported';
    if (syncState.kind === BACKGROUND) return 'onboarding.ia.sync.background';
    if (syncState.kind === FLUSHING) return 'onboarding.ia.sync.flushing';

    if (syncState.kind === READY) {
        if (!syncState.total) return null;
        return syncState.ready < syncState.total
            ? 'onboarding.ia.sync.ready_partial'
            : 'onboarding.ia.sync.ready';
    }

    // PREPARING. Con una cartera grande la espera puede ser larga y un «3 de 745» se lee como
    // «casi listo… nunca»: el copy lo dice desde el arranque en vez de dejar que se descubra.
    if (!syncState.total) return null;
    return syncState.largePortfolio
        ? 'onboarding.ia.sync.large_portfolio'
        : 'onboarding.ia.sync.preparing';
}

/**
 * Cuándo se avisa que una respuesta de ahora puede venir incompleta.
 *
 * Las dos fases donde el dato todavía no está: `PREPARING` (se está trayendo) y `BACKGROUND` (se
 * agotó la espera y sigue faltando). La segunda es la que más lo necesita y era la única que no lo
 * decía: el criterio de «listo» es el mismo que aplica el Data Node —cobertura completa de la
 * ventana—, así que pasado el tope la pantalla suelta al usuario con un «ya puedes preguntar»
 * sobre datos que el Data Node todavía puede rechazar. Soltarlo está bien; soltarlo sin avisar,
 * no.
 */
export function warnsAboutMissingData(syncState) {
    if (!syncState) return false;
    return syncState.kind === PREPARING || syncState.kind === BACKGROUND;
}
